import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import {
  Building2,
  LayoutDashboard,
  FileText,
  LogOut,
  Search,
  AlertTriangle,
  MessageSquare,
  Clock,
  TrendingUp,
  ChevronRight,
  Menu,
  X,
  Filter,
  Upload,
  Copy,
  CheckCircle2,
  Info,
  Lightbulb,
  ExternalLink,
  HelpCircle,
  Users,
  Download,
  QrCode,
  Settings,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import SubscriptionBanner from "../components/SubscriptionBanner";
import TrialExpiredScreen from "../components/TrialExpiredScreen";
import OnboardingWizard from "../components/OnboardingWizard";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Color palettes
const CATEGORIA_COLORS = {
  fraude: "#ef4444",
  assedio: "#f97316",
  seguranca: "#eab308",
  corrupcao: "#a855f7",
  outro: "#64748b",
};

const STATUS_COLORS = {
  recebida: "#3b82f6",
  em_analise: "#eab308",
  investigacao: "#a855f7",
  concluida: "#22c55e",
  arquivada: "#64748b",
};

const OUVIDORIA_TIPO_COLORS = {
  sugestao: "#22c55e",
  reclamacao: "#ef4444",
  elogio: "#3b82f6",
  informacao: "#a855f7",
};

const OUVIDORIA_STATUS_COLORS = {
  recebida: "#3b82f6",
  em_analise: "#eab308",
  respondida: "#22c55e",
  fechada: "#64748b",
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium text-[#1a1a2e]">{label || payload[0].name}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function EmpresaDashboard() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, empresa, logout, refreshEmpresa, isImpersonating, exitImpersonation, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check subscription/trial status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!slug) return;
      try {
        const response = await axios.get(`${API_URL}/empresas/${slug}/subscription-status`);
        setSubscriptionStatus(response.data);
      } catch (error) {
        console.error("Error checking subscription:", error);
      } finally {
        setCheckingSubscription(false);
      }
    };
    checkSubscription();
  }, [slug]);

  // Check if this is the first login - show onboarding wizard
  useEffect(() => {
    if (!user || !slug) return;
    
    const onboardingKey = `ombudnow_onboarding_${slug}_${user?.email}`;
    const hasCompletedOnboarding = localStorage.getItem(onboardingKey);
    
    if (!hasCompletedOnboarding) {
      // Show onboarding wizard on first login
      setShowOnboarding(true);
    }
  }, [user, slug]);

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!user) {
      toast.error("Sessão expirada. Por favor, faça login novamente.");
      navigate(`/${slug}/login`);
      return;
    }
    
    // CRITICAL: Verify the logged-in user belongs to this empresa
    // This prevents accessing /empresa-a/admin with credentials from empresa-b
    const checkEmpresaAccess = async () => {
      try {
        // Get the empresa info for the current slug
        const checkRes = await axios.get(`${API_URL}/empresas/${slug}/check`);
        if (!checkRes.data.exists) {
          toast.error("Empresa não encontrada.");
          navigate("/");
          return;
        }
        
        // If user has empresa_id, verify it matches
        // The empresa object from login response should match the slug
        if (empresa && empresa.slug !== slug) {
          toast.error("Você está logado em outra empresa. Por favor, faça login novamente.");
          localStorage.removeItem("token");
          localStorage.removeItem("empresa");
          navigate(`/${slug}/login`);
          return;
        }
        
        fetchData();
      } catch (error) {
        console.error("Error checking empresa access:", error);
        fetchData(); // Try to fetch anyway, API will return 403 if unauthorized
      }
    };
    
    checkEmpresaAccess();
  }, [user, slug, dateFilter, startDate, endDate, authLoading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      let params = {};
      if (dateFilter === "custom" && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (dateFilter !== "all") {
        params.period = dateFilter;
      }

      const [statsRes, timelineRes] = await Promise.all([
        axios.get(`${API_URL}/empresas/${slug}/dashboard/stats`, { params }),
        axios.get(`${API_URL}/empresas/${slug}/dashboard/timeline`, { params }),
      ]);
      
      setStats(statsRes.data);
      setTimeline(timelineRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Acesso negado. Você pode estar logado em outra empresa.");
        // Clear auth state to force re-login
        localStorage.removeItem("token");
        localStorage.removeItem("empresa");
        navigate(`/${slug}/login`);
      } else if (error.response?.status === 401) {
        toast.error("Sessão expirada. Por favor, faça login novamente.");
        localStorage.removeItem("token");
        localStorage.removeItem("empresa");
        navigate(`/${slug}/login`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/${slug}/admin/casos?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("logo", file);

    setUploadingLogo(true);
    try {
      await axios.put(`${API_URL}/empresas/${slug}/logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Logo atualizado com sucesso!");
      // Refresh empresa data to update logo_url
      await refreshEmpresa(slug);
    } catch (error) {
      toast.error("Erro ao atualizar logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  // Logo upload function for onboarding wizard
  const handleWizardLogoUpload = async (file) => {
    const formData = new FormData();
    formData.append("logo", file);

    await axios.put(`${API_URL}/empresas/${slug}/logo`, formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    // Refresh empresa data after wizard upload
    await refreshEmpresa(slug);
  };

  // Banner upload function
  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadingBanner(true);
    try {
      await axios.put(`${API_URL}/empresas/${slug}/banner`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(t("dashboard.bannerUpdated", "Banner atualizado com sucesso!"));
      // Refresh empresa data to update banner_url
      await refreshEmpresa(slug);
    } catch (error) {
      toast.error(t("dashboard.bannerError", "Erro ao atualizar banner"));
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleDateFilterChange = (value) => {
    setDateFilter(value);
    if (value !== "custom") {
      setStartDate("");
      setEndDate("");
    }
  };

  const handleLogout = () => {
    logout();
    navigate(`/${slug}/login`);
  };

  const copyToClipboard = (text, linkType) => {
    navigator.clipboard.writeText(text);
    setCopiedLink(linkType);
    toast.success("Link copiado!");
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const denunciaLink = `${window.location.origin}/${slug}/denuncia`;
  const ouvidoriaLink = `${window.location.origin}/${slug}/ouvidoria`;

  // Check if account is empty (no cases)
  const isEmptyAccount = stats && (stats.total_denuncias || 0) === 0 && (stats.total_ouvidoria || 0) === 0;

  const categoriaLabels = {
    fraude: t("dashboard.categories.fraud", "Fraude"),
    assedio: t("dashboard.categories.harassment", "Assédio"),
    seguranca: t("dashboard.categories.safety", "Segurança"),
    corrupcao: t("dashboard.categories.corruption", "Corrupção"),
    outro: t("dashboard.categories.other", "Outro"),
  };

  const statusLabels = {
    recebida: t("dashboard.status.received"),
    em_analise: t("dashboard.status.in_progress"),
    investigacao: t("dashboard.status.investigation", "Investigação"),
    concluida: t("dashboard.status.concluded"),
    arquivada: t("dashboard.status.archived"),
  };

  const ouvidoriaTipoLabels = {
    sugestao: t("dashboard.ombudsmanTypes.suggestion", "Sugestão"),
    reclamacao: t("dashboard.ombudsmanTypes.complaint", "Reclamação"),
    elogio: t("dashboard.ombudsmanTypes.compliment", "Elogio"),
    informacao: t("dashboard.ombudsmanTypes.information", "Informação"),
  };

  const ouvidoriaStatusLabels = {
    recebida: t("dashboard.status.received"),
    em_analise: t("dashboard.status.in_progress"),
    respondida: t("dashboard.status.answered", "Respondida"),
    fechada: t("dashboard.status.closed", "Fechada"),
  };

  const categoriaData = Object.entries(stats?.por_categoria || {}).map(([key, value]) => ({
    name: categoriaLabels[key] || key,
    value,
    color: CATEGORIA_COLORS[key] || "#64748b",
  }));

  const statusData = Object.entries(stats?.por_status || {}).map(([key, value]) => ({
    name: statusLabels[key] || key,
    value,
    color: STATUS_COLORS[key] || "#64748b",
  }));

  const ouvidoriaTipoData = Object.entries(stats?.ouvidoria_por_tipo || {}).map(([key, value]) => ({
    name: ouvidoriaTipoLabels[key] || key,
    value,
    color: OUVIDORIA_TIPO_COLORS[key] || "#64748b",
  }));

  const ouvidoriaStatusData = Object.entries(stats?.ouvidoria_por_status || {}).map(([key, value]) => ({
    name: ouvidoriaStatusLabels[key] || key,
    value,
    color: OUVIDORIA_STATUS_COLORS[key] || "#64748b",
  }));

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-xs font-medium">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Show loading while checking subscription
  if (checkingSubscription) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#f5a623] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Show trial expired screen if no access
  if (subscriptionStatus && !subscriptionStatus.has_access) {
    return <TrialExpiredScreen empresa={empresa} slug={slug} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-4 py-2 text-center flex items-center justify-center gap-4">
          <span className="text-sm font-medium">
            🔐 Modo Super Admin - Você está acessando como administrador de {empresa?.nome}
          </span>
          <button 
            onClick={exitImpersonation}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm font-medium transition-colors"
          >
            Sair do Modo Impersonate
          </button>
        </div>
      )}
      
      {/* Subscription Banner */}
      <SubscriptionBanner slug={slug} />
      
      <div className="flex flex-1">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {empresa?.logo_url ? (
                  <img 
                    src={empresa.logo_url.startsWith('data:') ? empresa.logo_url : (empresa.logo_url.startsWith('http') ? empresa.logo_url : `${process.env.REACT_APP_BACKEND_URL}${empresa.logo_url}`)} 
                    alt="" 
                    className="h-10 w-auto" 
                  />
                ) : (
                  <div className="w-10 h-10 bg-[#1a1a2e] rounded-lg flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                )}
                <div>
                  <h1 className="font-semibold text-[#1a1a2e] text-sm">{empresa?.nome}</h1>
                  <p className="text-xs text-slate-500">Compliance</p>
                </div>
              </div>
              <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Logo Upload */}
            <div className="mt-4 space-y-2">
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-[#1a1a2e] cursor-pointer"
              >
                <Upload className="w-3 h-3" />
                {uploadingLogo ? t("common.loading") : t("dashboard.changeLogo")}
              </label>
              
              {/* Banner Upload */}
              <input
                type="file"
                id="banner-upload"
                accept="image/*"
                onChange={handleBannerUpload}
                className="hidden"
              />
              <label
                htmlFor="banner-upload"
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-[#1a1a2e] cursor-pointer"
              >
                <Upload className="w-3 h-3" />
                {uploadingBanner ? t("common.loading") : t("dashboard.changeBanner", "Alterar Banner")}
              </label>
              <p className="text-[10px] text-slate-400">
                {t("dashboard.bannerSize", "Banner: 1920x890px")}
              </p>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            <Link
              to={`/${slug}/admin`}
              className="flex items-center gap-3 px-4 py-3 text-[#1a1a2e] bg-slate-100 rounded-lg font-medium"
              data-testid="sidebar-dashboard"
            >
              <LayoutDashboard className="w-5 h-5" />
              {t("dashboard.menu.dashboard")}
            </Link>
            <Link
              to={`/${slug}/admin/casos`}
              className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
              data-testid="sidebar-casos"
            >
              <FileText className="w-5 h-5" />
              {t("dashboard.menu.cases")}
            </Link>
            {(user?.role === "admin" || user?.role === "gestor") && (
              <Link
                to={`/${slug}/admin/investigadores`}
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                data-testid="sidebar-investigadores"
              >
                <Users className="w-5 h-5" />
                {t("dashboard.menu.investigators")}
              </Link>
            )}
            {user?.role === "admin" && (
              <Link
                to={`/${slug}/admin/configuracoes`}
                className="flex items-center gap-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                data-testid="sidebar-configuracoes"
              >
                <Settings className="w-5 h-5" />
                {t("dashboard.menu.settings", "Configurações")}
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#1a1a2e] rounded-full flex items-center justify-center text-white font-medium">
                {user?.nome?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1a1a2e] truncate">{user?.nome}</p>
                <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
              </div>
            </div>
            <Button variant="outline" className="w-full justify-start" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              {t("dashboard.menu.logout")}
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-6 h-6" />
              </button>
              <form onSubmit={handleSearch} className="hidden md:block">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("dashboard.searchProtocol")}
                    className="pl-10 w-80"
                  />
                </div>
              </form>
            </div>
          </div>
        </header>

        <div className="flex-1 p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a1a2e]"></div>
            </div>
          ) : (
            <>
              {/* Header with Date Filters */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-[#1a1a2e] mb-1">{t("dashboard.greeting")}, {user?.nome?.split(" ")[0]}!</h1>
                  <p className="text-slate-600">{t("dashboard.overview")}</p>
                </div>

                <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-500" />
                    <Label className="text-sm font-medium text-slate-600">{t("dashboard.filters.period", "Período")}:</Label>
                  </div>
                  <Select value={dateFilter} onValueChange={handleDateFilterChange}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder={t("dashboard.filters.select", "Selecione")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t("dashboard.filters.all", "Todos")}</SelectItem>
                      <SelectItem value="7d">{t("dashboard.filters.last7days", "Últimos 7 dias")}</SelectItem>
                      <SelectItem value="30d">{t("dashboard.filters.last30days", "Últimos 30 dias")}</SelectItem>
                      <SelectItem value="90d">{t("dashboard.filters.last90days", "Últimos 90 dias")}</SelectItem>
                      <SelectItem value="year">{t("dashboard.filters.thisYear", "Este ano")}</SelectItem>
                      <SelectItem value="custom">{t("dashboard.filters.custom", "Personalizado")}</SelectItem>
                    </SelectContent>
                  </Select>

                  {dateFilter === "custom" && (
                    <div className="flex items-center gap-2">
                      <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-[140px]" />
                      <span className="text-slate-400">{t("dashboard.filters.to", "até")}</span>
                      <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-[140px]" />
                    </div>
                  )}
                </div>
              </div>

              {/* Recommended Next Step - Only show for empty accounts */}
              {isEmptyAccount && (
                <div className="bg-gradient-to-r from-[#1a1a2e] to-[#2d2d4a] rounded-xl p-6 mb-8 text-white" data-testid="next-step-block">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-[#f5a623]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                        <span>{t("dashboard.onboarding.title")}</span>
                        <span className="text-xs bg-[#f5a623] text-[#1a1a2e] px-2 py-0.5 rounded-full font-medium">{t("dashboard.onboarding.new")}</span>
                      </h3>
                      <p className="text-white/80 mb-4">
                        {t("dashboard.onboarding.description")}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="flex-1 bg-white/10 rounded-lg p-3">
                          <p className="text-xs text-white/60 mb-1">{t("dashboard.onboarding.whistleblowingChannel")}</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm flex-1 truncate">{denunciaLink}</code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white hover:bg-white/20 h-8"
                              onClick={() => copyToClipboard(denunciaLink, "denuncia")}
                              data-testid="copy-denuncia-link"
                            >
                              {copiedLink === "denuncia" ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                        <div className="flex-1 bg-white/10 rounded-lg p-3">
                          <p className="text-xs text-white/60 mb-1">{t("dashboard.onboarding.ombudsmanChannel")}</p>
                          <div className="flex items-center gap-2">
                            <code className="text-sm flex-1 truncate">{ouvidoriaLink}</code>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-white hover:bg-white/20 h-8"
                              onClick={() => copyToClipboard(ouvidoriaLink, "ouvidoria")}
                              data-testid="copy-ouvidoria-link"
                            >
                              {copiedLink === "ouvidoria" ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-white/50 mt-3 flex items-center gap-1">
                        <Info className="w-3 h-3" />
                        {t("dashboard.onboarding.copyHint")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Cards */}
              <TooltipProvider>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="stat-denuncias">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#1a1a2e]/10 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-[#1a1a2e]" />
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{t("dashboard.tooltips.totalWhistleblowing")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.total_denuncias || 0}</p>
                  <p className="text-sm text-slate-500">{t("dashboard.stats.totalWhistleblowing")}</p>
                  {(stats?.total_denuncias || 0) === 0 && (
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {t("dashboard.stats.noWhistleblowing")}
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="stat-ouvidoria">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#f5a623]/10 rounded-xl flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-[#f5a623]" />
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{t("dashboard.tooltips.totalOmbudsman")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.total_ouvidoria || 0}</p>
                  <p className="text-sm text-slate-500">{t("dashboard.stats.totalOmbudsman")}</p>
                  {(stats?.total_ouvidoria || 0) === 0 && (
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                      <Info className="w-3 h-3" />
                      {t("dashboard.stats.noOmbudsman")}
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="stat-abertos">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{t("dashboard.tooltips.openCases")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.casos_abertos || 0}</p>
                  <p className="text-sm text-slate-500">{t("dashboard.stats.openCases")}</p>
                  {(stats?.casos_abertos || 0) === 0 && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {t("dashboard.stats.allOnTime")}
                    </p>
                  )}
                </div>

                <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="stat-sla">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                    <Tooltip>
                      <TooltipTrigger>
                        <HelpCircle className="w-4 h-4 text-slate-400 hover:text-slate-600" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{t("dashboard.tooltips.slaExpired")}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-3xl font-bold text-[#1a1a2e]">{stats?.casos_vencidos || 0}</p>
                  <p className="text-sm text-slate-500">{t("dashboard.stats.slaExpired")}</p>
                  {(stats?.casos_vencidos || 0) === 0 && (
                    <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      {t("dashboard.stats.noExpired")}
                    </p>
                  )}
                </div>
              </div>
              </TooltipProvider>

              {/* Timeline Chart */}
              {timeline.length > 0 && (
                <div className="bg-white rounded-xl border border-slate-200 p-6 mb-8">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("dashboard.charts.evolution", "Evolução de Casos")}</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" stroke="#64748b" fontSize={12} />
                      <YAxis stroke="#64748b" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Line type="monotone" dataKey="denuncias" name={t("dashboard.recentCases.whistleblowing")} stroke="#1a1a2e" strokeWidth={2} dot={{ fill: "#1a1a2e", r: 4 }} />
                      <Line type="monotone" dataKey="ouvidoria" name={t("dashboard.recentCases.ombudsman")} stroke="#f5a623" strokeWidth={2} dot={{ fill: "#f5a623", r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Charts Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Denúncias by Category */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("dashboard.charts.byCategory")}</h3>
                  {categoriaData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={categoriaData} cx="50%" cy="50%" labelLine={false} label={renderCustomizedLabel} outerRadius={100} dataKey="value">
                          {categoriaData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">Nenhuma denúncia no período</div>
                  )}
                </div>

                {/* Denúncias by Status */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("dashboard.charts.byStatus")}</h3>
                  {statusData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={statusData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={100} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" name={t("dashboard.charts.quantity", "Quantidade")} radius={[0, 4, 4, 0]}>
                          {statusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">{t("dashboard.charts.noDataPeriod", "Nenhuma denúncia no período")}</div>
                  )}
                </div>

                {/* Ouvidoria by Tipo */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("dashboard.charts.ombudsmanByType", "Ouvidoria por Tipo")}</h3>
                  {ouvidoriaTipoData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={ouvidoriaTipoData} cx="50%" cy="50%" labelLine={false} label={renderCustomizedLabel} outerRadius={100} dataKey="value">
                          {ouvidoriaTipoData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">{t("dashboard.charts.noOmbudsmanPeriod", "Nenhuma ouvidoria no período")}</div>
                  )}
                </div>

                {/* Ouvidoria by Status */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <h3 className="font-semibold text-[#1a1a2e] mb-4">{t("dashboard.charts.ombudsmanByStatus", "Ouvidoria por Status")}</h3>
                  {ouvidoriaStatusData.some(d => d.value > 0) ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={ouvidoriaStatusData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" stroke="#64748b" fontSize={12} />
                        <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={12} width={100} />
                        <RechartsTooltip content={<CustomTooltip />} />
                        <Bar dataKey="value" name={t("dashboard.charts.quantity", "Quantidade")} radius={[0, 4, 4, 0]}>
                          {ouvidoriaStatusData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[250px] flex items-center justify-center text-slate-500">{t("dashboard.charts.noOmbudsmanPeriod", "Nenhuma ouvidoria no período")}</div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="quick-actions">
                <h3 className="font-semibold text-[#1a1a2e] mb-2">{t("dashboard.quickActions.title", "Ações Rápidas")}</h3>
                <p className="text-sm text-slate-500 mb-4">{t("dashboard.quickActions.subtitle", "Acesse rapidamente as áreas mais importantes do sistema.")}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Link to={`/${slug}/admin/casos?tipo=denuncia&status=recebida`} className="flex flex-col p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group" data-testid="action-novas-denuncias">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-[#1a1a2e]" />
                        <span className="font-medium text-[#1a1a2e]">{t("dashboard.quickActions.newReports", "Novas Denúncias")}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <p className="text-xs text-slate-500 pl-8">{t("dashboard.quickActions.newReportsDesc", "Denúncias aguardando triagem inicial")}</p>
                  </Link>

                  <Link to={`/${slug}/admin/casos?tipo=ouvidoria&status=recebida`} className="flex flex-col p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group" data-testid="action-ouvidoria-pendente">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <MessageSquare className="w-5 h-5 text-[#f5a623]" />
                        <span className="font-medium text-[#1a1a2e]">{t("dashboard.quickActions.pendingOmbudsman", "Ouvidoria Pendente")}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <p className="text-xs text-slate-500 pl-8">{t("dashboard.quickActions.pendingOmbudsmanDesc", "Manifestações que precisam de resposta")}</p>
                  </Link>

                  <Link to={`/${slug}/admin/casos`} className="flex flex-col p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group" data-testid="action-todos-casos">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-[#0EA5E9]" />
                        <span className="font-medium text-[#1a1a2e]">{t("dashboard.quickActions.viewAll", "Ver Todos os Casos")}</span>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                    </div>
                    <p className="text-xs text-slate-500 pl-8">{t("dashboard.quickActions.viewAllDesc", "Lista completa com filtros avançados")}</p>
                  </Link>
                </div>
              </div>

              {/* Channel Links Card - Always visible for easy access */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 mt-6" data-testid="channel-links">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-[#1a1a2e]">{t("dashboard.channelLinks.title", "Links dos Canais")}</h3>
                    <p className="text-sm text-slate-500">{t("dashboard.channelLinks.subtitle", "Compartilhe esses links com os colaboradores")}</p>
                  </div>
                  <QrCode className="w-5 h-5 text-slate-400" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Whistleblowing Channel */}
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-4 h-4 text-[#1a1a2e]" />
                      <p className="text-sm font-medium text-[#1a1a2e]">{t("dashboard.channelLinks.whistleblowing", "Canal de Denúncias (Anônimo)")}</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                        <QRCodeSVG 
                          value={denunciaLink} 
                          size={120}
                          level="H"
                          includeMargin={false}
                          fgColor="#1a1a2e"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 mb-2 break-all">{denunciaLink}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(denunciaLink, "denuncia2")}
                            data-testid="copy-denuncia-link-2"
                          >
                            {copiedLink === "denuncia2" ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            <span className="ml-1">{copiedLink === "denuncia2" ? "Copiado" : "Copiar"}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const svg = document.getElementById('qr-denuncia');
                              if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                img.onload = () => {
                                  canvas.width = img.width;
                                  canvas.height = img.height;
                                  ctx.fillStyle = 'white';
                                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                                  ctx.drawImage(img, 0, 0);
                                  const a = document.createElement('a');
                                  a.download = `qrcode-denuncia-${slug}.png`;
                                  a.href = canvas.toDataURL('image/png');
                                  a.click();
                                };
                                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                              }
                            }}
                            data-testid="download-qr-denuncia"
                          >
                            <Download className="w-4 h-4" />
                            <span className="ml-1">QR</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="hidden">
                      <QRCodeSVG 
                        id="qr-denuncia"
                        value={denunciaLink} 
                        size={300}
                        level="H"
                        includeMargin={true}
                        fgColor="#1a1a2e"
                      />
                    </div>
                  </div>
                  
                  {/* Ombudsman Channel */}
                  <div className="p-4 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-[#f5a623]" />
                      <p className="text-sm font-medium text-[#1a1a2e]">{t("dashboard.channelLinks.ombudsman", "Canal de Ouvidoria (Identificado)")}</p>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200">
                        <QRCodeSVG 
                          value={ouvidoriaLink} 
                          size={120}
                          level="H"
                          includeMargin={false}
                          fgColor="#1a1a2e"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 mb-2 break-all">{ouvidoriaLink}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(ouvidoriaLink, "ouvidoria2")}
                            data-testid="copy-ouvidoria-link-2"
                          >
                            {copiedLink === "ouvidoria2" ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            <span className="ml-1">{copiedLink === "ouvidoria2" ? "Copiado" : "Copiar"}</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const svg = document.getElementById('qr-ouvidoria');
                              if (svg) {
                                const svgData = new XMLSerializer().serializeToString(svg);
                                const canvas = document.createElement('canvas');
                                const ctx = canvas.getContext('2d');
                                const img = new Image();
                                img.onload = () => {
                                  canvas.width = img.width;
                                  canvas.height = img.height;
                                  ctx.fillStyle = 'white';
                                  ctx.fillRect(0, 0, canvas.width, canvas.height);
                                  ctx.drawImage(img, 0, 0);
                                  const a = document.createElement('a');
                                  a.download = `qrcode-ouvidoria-${slug}.png`;
                                  a.href = canvas.toDataURL('image/png');
                                  a.click();
                                };
                                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                              }
                            }}
                            data-testid="download-qr-ouvidoria"
                          >
                            <Download className="w-4 h-4" />
                            <span className="ml-1">QR</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="hidden">
                      <QRCodeSVG 
                        id="qr-ouvidoria"
                        value={ouvidoriaLink} 
                        size={300}
                        level="H"
                        includeMargin={true}
                        fgColor="#1a1a2e"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
      </div>

      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        empresa={empresa}
        slug={slug}
        user={user}
        onLogoUpload={handleWizardLogoUpload}
        onComplete={() => {
          setShowOnboarding(false);
          toast.success(
            t("dashboard.greeting") + ", " + user?.nome?.split(" ")[0] + "!",
            { description: t("dashboard.overview"), duration: 5000 }
          );
        }}
      />
    </div>
  );
}
